import posthog, { type PostHogConfig } from 'posthog-js';
import type { UserRole } from '@doorly/shared';

/**
 * Analytics produit Doorly — couche typée au-dessus de posthog-js.
 *
 * Conventions :
 *  - Identification par `userId` Doorly (pas l'email Clerk) → cohérent avec
 *    l'archi d'anonymisation B2B et minimise les PII chez PostHog.
 *  - Persistence localStorage uniquement → pas de cookies, pas de banner RGPD.
 *  - Autocapture désactivé → on track explicitement les events de cette
 *    union pour garder la signal/noise élevée et le free tier soutenable.
 *  - Session replay activé avec masquage agressif des inputs (PII des leads).
 *
 * Ne s'initialise qu'en prod (Vercel) — dev est silent par défaut pour ne pas
 * polluer les funnels avec des sessions de test. Pour tester côté dev, ajoute
 * `VITE_POSTHOG_DEV=1` dans `.env.local`.
 */

type EventMap = {
  /** Wizard ouvert avec une sélection initiale de verticales. */
  wizard_started: {
    verticales: string[];
    verticaleCount: number;
  };
  /** Une phase du wizard est complétée (passage à la suivante). */
  wizard_step_completed: {
    step: string;
    durationMs: number;
  };
  /** L'utilisateur quitte le wizard avant submit (unmount sans recap). */
  wizard_abandoned: {
    lastStep: string;
    timeSpentMs: number;
  };
  /** Un lead est créé localement (avant sync) — un event par lead. */
  lead_created: {
    verticale: string;
    hasGeoloc: boolean;
    hasBanId: boolean;
    hasContactGroup: boolean;
  };
  /** L'admin démarre une session d'impersonation. */
  imperso_started: {
    targetRole: UserRole;
    targetPartnerId: string | null;
  };
  /** Fin de session d'impersonation. */
  imperso_stopped: Record<string, never>;
  /** Bascule de vendeur par PIN sur tablette partagée. `verified` : où le code a été comparé. */
  pin_switch: { verified: 'server' | 'device' };
  pin_switch_locked: Record<string, never>;
  pin_set: Record<string, never>;
  /** L'utilisateur a accepté l'install PWA (event `appinstalled`). */
  pwa_installed: Record<string, never>;
  /** L'utilisateur a cliqué "Recharger" sur le toast de mise à jour. */
  update_reloaded: Record<string, never>;
  /** Auto-reload silencieux déclenché par le SW pendant la fenêtre
   *  post-login (cf. main.tsx — auto-reload safe car pas en saisie). */
  update_auto_reloaded_post_login: Record<string, never>;
};

export type AnalyticsEvent = keyof EventMap;

let initialized = false;

export function initAnalytics(): void {
  if (initialized) return;
  const key = import.meta.env.VITE_POSTHOG_KEY;
  const host = import.meta.env.VITE_POSTHOG_HOST;
  const enableInDev = import.meta.env.VITE_POSTHOG_DEV === '1';
  if (!key || !host) return;
  if (!import.meta.env.PROD && !enableInDev) return;

  const config: Partial<PostHogConfig> = {
    api_host: host,
    persistence: 'localStorage',
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: false,
    session_recording: {
      // Masque tout texte saisi par l'utilisateur dans les inputs (lead PII,
      // notes, recherches). Affiche le DOM sans le contenu sensible.
      maskAllInputs: true,
      // Sélecteurs explicites à floutter / bloquer côté composants quand
      // nécessaire — `[data-ph-mask]` floute, `[data-ph-no-capture]` bloque
      // entièrement le sous-arbre.
      maskTextSelector: '[data-ph-mask]',
      blockSelector: '[data-ph-no-capture]',
    },
    loaded: (ph) => {
      if (!import.meta.env.PROD) ph.debug();
    },
  };

  posthog.init(key, config);
  // Super-properties attachées automatiquement à TOUS les events et au
  // profil user → permet de filtrer dans PostHog par version d'app, et
  // de détecter les users coincés sur une vieille version (cf. fix
  // auto-reload SW PR #340/#342). `$set` propage aussi sur le profil.
  posthog.register({
    app_version: __APP_VERSION__,
    app_sha: __APP_SHA__,
    app_build_date: __APP_BUILD_DATE__,
  });
  posthog.people.set({
    app_version: __APP_VERSION__,
    app_sha: __APP_SHA__,
    app_last_seen_at: new Date().toISOString(),
  });
  initialized = true;
}

/**
 * Lie le user authentifié à toutes les events suivants. À appeler une fois
 * que `me` (Postgres profile) est chargé. ID = userId Doorly stable, pas
 * l'email Clerk, pour cohérence avec l'archi d'anonymisation.
 */
export function identifyAnalytics(
  userId: string,
  props: { role: UserRole; partnerId: string },
): void {
  if (!initialized) return;
  posthog.identify(userId, {
    role: props.role,
    partnerId: props.partnerId,
    // Mise à jour de la version vue pour cet user à chaque login → permet
    // de répondre à "quels users sont restés bloqués sur une version
    // d'il y a 5 jours ?" via une cohorte PostHog (app_version != latest).
    app_version: __APP_VERSION__,
    app_sha: __APP_SHA__,
    app_last_seen_at: new Date().toISOString(),
  });
}

/**
 * Purge l'identité locale + cookies PostHog. À appeler au sign-out.
 */
export function resetAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
}

/**
 * Track typé — la signature force le shape des props par event name.
 * Throw silencieux en dev si PostHog pas init (le helper retourne
 * simplement sans logger pour ne pas polluer la console).
 */
export function track<K extends AnalyticsEvent>(name: K, props: EventMap[K]): void {
  if (!initialized) return;
  posthog.capture(name, props);
}
